import { PaymentSummaryCard, PaymentSummaryCardSkeleton } from '@/components/molecules';
import PaymentDetailsDialog from '@/components/organisms/PaymentDetailsDialog';
import WebAppLayout from '@/components/organisms/WebAppLayout';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useAppStore } from '@/core/stores/appStore';
import { getUserFirstName } from '@/core/utils';
import { isSmallScreen } from '@/core/utils/responsive';
import {
    getAllOverdueInstallments,
    getAllUpcomingDueInstallments,
    getMostRecentPaidInstallment,
    getPropertiesWithCheques,
    getTotalPropertyPaymentCounts,
    getTotalServiceChargeCounts,
    getTotalServiceChargeOutstanding,
    selectOnlinePaymentIds,
} from '@/modules/payments/utils/installmentUtils';
import { openServiceChargePayments } from '@/modules/payments/utils/openServiceChargePayments';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function PaymentsScreenWeb() {
    useCatalogDynatraceView(DT_SCREEN.PAYMENTS, 'payments_tab_viewed', true);
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const isMobile = isSmallScreen();
    const [paymentDetailsVisible, setPaymentDetailsVisible] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);

    // Get real data from store
    const user = useAppStore((state) => state.auth.user);
    const properties = useAppStore((state) => state.properties);
    const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);
    const unitInstallments = useAppStore((state) => state.unitInstallments);
    const profileLoading = useAppStore((state) => state.profileLoading);
    const unitInstallmentsLoading = useAppStore((state) => state.unitInstallmentsLoading);

    const firstName = getUserFirstName(user);
    const userEmail = user?.primary_email || user?.secondary_email || user?.tertiary_email || '';

    // Calculate payment summary data from real installments (reused from mobile PaymentsScreen)
    const paymentSummaryData = useMemo(() => {
        const propertyPaymentTotals = getTotalPropertyPaymentCounts(unitInstallments, properties);
        const chequesData = getPropertiesWithCheques(unitInstallments, properties);
        const mostRecentPropertyPayment = getMostRecentPaidInstallment(unitInstallments, properties, 'installment');

        const serviceChargeTotals = getTotalServiceChargeCounts(unitInstallments, properties);
        const serviceChargeOutstanding = getTotalServiceChargeOutstanding(unitInstallments, properties);
        const mostRecentServiceCharge = getMostRecentPaidInstallment(unitInstallments, properties, 'other_charge');

        const overdueServiceCharges = getAllOverdueInstallments(unitInstallments, properties).filter(
            (item) => item.payment_type === 'other_charge'
        );
        const upcomingServiceCharges = getAllUpcomingDueInstallments(unitInstallments, properties).filter(
            (item) => item.payment_type === 'other_charge'
        );

        const allServiceChargePaymentIds = [
            ...overdueServiceCharges.map((item) => item.id),
            ...upcomingServiceCharges.map((item) => item.id),
        ];
        const payableServiceChargePaymentIds = selectOnlinePaymentIds([
            ...overdueServiceCharges,
            ...upcomingServiceCharges,
        ]);

        const propertyPayments = {
            id: 'property-payments',
            title: t('payments.propertyPayments'),
            subtitle: `${properties.length} ${
                properties.length === 1 ? t('payments.property') : t('payments.properties')
            }`,
            missedCount: propertyPaymentTotals.overdue,
            dueCount: propertyPaymentTotals.upcomingDue,
            totalOutstanding: undefined as number | undefined,
            cheques: chequesData.map((cheque) => ({
                id: cheque.propertyId,
                propertyName: cheque.propertyName,
                chequeCount: `${cheque.chequeCount} ${
                    cheque.chequeCount === 1 ? t('payments.cheque') : t('payments.cheques')
                }`,
                amount: cheque.totalAmount,
            })),
            lastPayment: {
                id: 'last-payment',
                propertyName:
                    mostRecentPropertyPayment?.propertyName ||
                    properties[0]?.unitNumber ||
                    properties[0]?.projectName ||
                    'N/A',
                paymentDate: mostRecentPropertyPayment
                    ? new Date(mostRecentPropertyPayment.paymentDate).toLocaleDateString('en-GB')
                    : new Date().toLocaleDateString('en-GB'),
                paymentDescription: t('payments.lastPayment'),
                amount: mostRecentPropertyPayment?.paid_amount || mostRecentPropertyPayment?.amount || 0,
            },
        };

        const serviceChargePayments = {
            id: 'service-charge-payments',
            title: t('payments.serviceChargePayments'),
            subtitle: `${properties.length} ${
                properties.length === 1 ? t('payments.property') : t('payments.properties')
            }`,
            missedCount: serviceChargeTotals.overdue,
            dueCount: serviceChargeTotals.upcomingDue,
            totalOutstanding: serviceChargeOutstanding,
            cheques: [],
            paymentIds: allServiceChargePaymentIds,
            payablePaymentIds: payableServiceChargePaymentIds,
            lastPayment: {
                id: 'service-charge-last-payment',
                propertyName:
                    mostRecentServiceCharge?.propertyName ||
                    properties[0]?.unitNumber ||
                    properties[0]?.projectName ||
                    'N/A',
                paymentDate: mostRecentServiceCharge
                    ? new Date(mostRecentServiceCharge.paymentDate).toLocaleDateString('en-GB')
                    : new Date().toLocaleDateString('en-GB'),
                paymentDescription: t('payments.serviceChargePayments'),
                amount: mostRecentServiceCharge?.paid_amount || mostRecentServiceCharge?.amount || 0,
            },
        };

        return [propertyPayments, serviceChargePayments];
    }, [unitInstallments, properties, t]);

    return (
        <WebAppLayout activeTab="payments" headerTitle={t('payments.title')} showBackButton={false}>
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
                {/* Main Content */}
                <View style={styles.mainContent}>
                    <ScrollView style={styles.contentArea} contentContainerStyle={styles.contentScroll}>

                    {/* Payment summary cards */}
                    <View style={styles.paymentCardsContainer}>
                        {(profileLoading || unitInstallmentsLoading) && paymentSummaryData.length === 0 ? (
                            <>
                                <PaymentSummaryCardSkeleton />
                                <PaymentSummaryCardSkeleton />
                            </>
                        ) : (
                            paymentSummaryData.map((paymentData) => (
                                <PaymentSummaryCard
                                    key={paymentData.id}
                                    title={paymentData.title}
                                    subtitle={paymentData.subtitle}
                                    missedCount={paymentData.missedCount}
                                    dueCount={paymentData.dueCount}
                                    {...(paymentData.totalOutstanding !== undefined && {
                                        totalOutstanding: paymentData.totalOutstanding,
                                    })}
                                    onPress={() => {
                                        trackCatalogEvent({
                                            screen: DT_SCREEN.PAYMENTS,
                                            actionName: 'payments_tab_property_select',
                                            eventType: 'action',
                                            properties: { propertyId: paymentData.id },
                                        });
                                        if (paymentData.id !== 'service-charge-payments') {
                                            router.push('/(auth)/property-payments' as any);
                                            return;
                                        }
                                        const paymentIds = (paymentData as { paymentIds?: string[] }).paymentIds ?? [];
                                        const selectableIds = isAdhaJourney
                                            ? paymentIds
                                            : ((paymentData as { payablePaymentIds?: string[] }).payablePaymentIds ?? []);
                                        openServiceChargePayments({
                                            paymentIds,
                                            selectableIds,
                                            isWeb: Platform.OS === 'web',
                                            openSingleOnWeb: (paymentId) => {
                                                setSelectedPaymentId(paymentId);
                                                setPaymentDetailsVisible(true);
                                            },
                                            openSingleNative: (paymentId) => {
                                                router.push(`/(auth)/payment-details?id=${paymentId}` as any);
                                            },
                                            openSelectedList: (selectedPaymentIds) => {
                                                router.push(
                                                    `/(auth)/property-payments?tab=upcoming&selectedPaymentIds=${selectedPaymentIds.join(',')}` as any
                                                );
                                            },
                                            openEmptyList: () => {
                                                router.push('/(auth)/property-payments' as any);
                                            },
                                        });
                                    }}
                                />
                            ))
                        )}
                    </View>
                </ScrollView>
            </View>
            {/* Payment details dialog for web – route is not changed */}
            {Platform.OS === 'web' && selectedPaymentId && (
                <PaymentDetailsDialog
                    visible={paymentDetailsVisible}
                    paymentId={selectedPaymentId}
                    onClose={() => setPaymentDetailsVisible(false)}
                />
            )}
        </View>
        </WebAppLayout>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    mainContent: {
        flex: 1,
    },
    contentArea: {
        flex: 1,
    },
    contentScroll: {
        gap: 24,
        paddingBottom: 24,
    },
    greetingSection: {
        marginBottom: 8,
    },
    paymentCardsContainer: {
        gap: 16,
    },
});


